<script>
/**
 * @component ModalAppointment
 * @description Modal de agendamiento/visualización de citas médicas.
 *              Diseño card-based con secciones, búsqueda de pacientes con avatar.
 * @example <ModalAppointment :showDialogModal="true" :selectedSlot="slot" @close="close" />
 */
import helperService from "@/services/helper";
import patientService from "@/services/patient";
import appointmentService from "@/services/appointment";
import { getMedicalInsuranceNumber } from "@/services/patient-utils";
import {
  APPOINTMENTS_TYPES,
  VIDEOROOM_MM_OFFSET_PRE,
  VIDEOROOM_MM_OFFSET_POST,
} from "@/services/constants";
import { addMilliseconds } from "date-fns";
import { formatTimeAR, parseUserInputAR, toApiUTC } from "@/services/datetime";
import pagination from "@/mixins/pagination";
import Spinner from "@/components/ui/Spinner";
import PatientQuickForm from "@/components/patient/PatientQuickForm.vue";
import { getPatientBlockReason } from "@/services/appointment-modal";
import { getAppointmentStatus } from "@/services/appointment-status";
import { formatCoverage } from "@/services/coverage";
import observationService, {
  OBSERVATION_MAX_LENGTH,
} from "@/services/appointment-observation";
import debounce from "debounce";
import institutionService from "@/services/institution";
import { isVideoRoomEnabled } from "@/services/config";
import { mapGetters } from "vuex";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "ModalAppointment",

  mixins: [pagination],

  components: {
    Spinner,
    PatientQuickForm,
  },

  props: {
    showDialogModal: {
      type: Boolean,
      default: false,
    },
    selectedSlot: {
      type: Object,
    },
    selectedAppointment: {
      type: Object,
    },
    selectedMedicalSpecialty: {
      type: Object,
    },
  },

  data() {
    return {
      appointmentTypes: APPOINTMENTS_TYPES,
      attentionTypes: [],
      patients: [],
      searchInput: "",
      selectedPatient: null,
      appointment: {
        slot: this.selectedSlot,
        patient: null,
        medicalSpecialty: this.selectedMedicalSpecialty?._id,
        institution: this.selectedSlot.institution,
        description: "",
        status: "",
        attentionType: "",
        videoRoom: null,
      },
      institution: null,
      isNewAppointment: !this.selectedSlot.appointment,
      isPosting: false,
      isDeleting: false,
      /** @type {boolean} Indica si se está admitiendo al paciente */
      admitting: false,
      isLoading: false,
      customStartTime: new Date(),
      customDuration: 15,
      /**
       * @description UC-REG-130 — Cuando true, el panel de búsqueda de
       *              paciente se reemplaza por <PatientQuickForm> para
       *              crear uno desde el mismo modal del turno.
       */
      creatingPatient: false,
      /**
       * @description UC-REG-275 — Observación del turno: `observation` es el
       *              borrador (al agendar o editando) y `savedObservation` la
       *              guardada en el API (null si no hay).
       */
      observation: { text: "", visibleToPatient: false },
      savedObservation: null,
      editingObservation: false,
      savingObservation: false,
      observationMaxLength: OBSERVATION_MAX_LENGTH,
    };
  },

  computed: {
    ...mapGetters(["isPatient", "user"]),
    isInstantAppointment() {
      return !this.selectedSlot._id;
    },

    /**
     * @description UC-REG-273: estado de la cita asignada (null si es nueva).
     * @returns {{key: string, label: string, className: string}|null}
     */
    appointmentStatus() {
      if (this.isNewAppointment || !this.appointment.status) return null;
      return getAppointmentStatus({
        ...this.appointment,
        endsAt: this.appointment.endsAt || this.selectedSlot.endsAt,
      });
    },

    /**
     * @description UC-REG-274: cobertura del paciente de la cita.
     * @returns {string}
     */
    patientCoverage() {
      return formatCoverage(this.selectedPatient);
    },

    isVideoRoomAvailable() {
      if (!this.appointment) {
        return false;
      }

      const now = new Date();

      const offsetStartsAt = new Date(this.appointment.startsAt);
      const offsetEndsAt = new Date(this.appointment.endsAt);

      offsetStartsAt.setMinutes(
        offsetStartsAt.getMinutes() - VIDEOROOM_MM_OFFSET_PRE
      );
      offsetEndsAt.setMinutes(
        offsetEndsAt.getMinutes() + VIDEOROOM_MM_OFFSET_POST
      );

      return (
        this.appointment.attentionType === "virtual" &&
        offsetStartsAt <= now &&
        offsetEndsAt >= now &&
        isVideoRoomEnabled
      );
    },

    canCancelAppointment() {
      if (this.isPatient) {
        return this.appointment.patient === this.user._id;
      } else {
        return true;
      }
    },
  },

  created() {
    if (this.isNewAppointment) {
      !this.isPatient && this.getPatients();
      this.appointment.status = "pending";
    } else {
      if (!this.isNewAppointment) {
        this.getAppointment();
      }
    }
    this.getInstitution();
    this.setAttentionTypes();
  },

  methods: {
    async getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      const query = {
        offset: 0,
        limit: this.pagination.limit,
      };

      const input = this.searchInput && this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);

      // Search up to 3 words (Double names or double last names)
      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useSearchIndex) {
        query.$text = {
          $search: this.searchInput,
        };
      } else if (useNameIndex) {
        Object.assign(query, helperService.createNameQuery(this.searchInput));
      }

      this.getPatients(query);
    }, DEBOUNCE_MS_TIMEOUT),

    async getAppointment() {
      this.appointment = await appointmentService.getAppointmentById({
        id: this.selectedSlot.appointment?._id || this.selectedSlot.appointment,
        params: { populate: ["institution", "medicalSpecialty"] },
      });
      await Promise.all([this.getPatientById(), this.loadObservation()]);
    },

    /**
     * @description UC-REG-275 — Trae la observación del turno (el paciente
     *              solo recibe la suya si está marcada como visible).
     */
    async loadObservation() {
      const [observation] = await observationService.getByAppointments([
        this.appointment._id,
      ]);
      this.savedObservation = observation || null;
    },

    /**
     * @description UC-REG-275 — Pasa la observación a modo edición.
     */
    startEditObservation() {
      this.observation = {
        text: this.savedObservation?.text || "",
        visibleToPatient: !!this.savedObservation?.visibleToPatient,
      };
      this.editingObservation = true;
    },

    /**
     * @description UC-REG-275 — Guarda la observación editada (texto vacío
     *              la borra) y avisa al calendario / Atenciones.
     */
    async saveObservation() {
      this.savingObservation = true;
      try {
        const saved = await observationService.save(
          this.appointment._id,
          this.observation
        );
        this.savedObservation = saved && saved.deleted ? null : saved;
        this.editingObservation = false;
        this.$bus.$emit(
          "appointment-observation-saved",
          this.appointment._id,
          this.savedObservation
        );
        this.$message.success("Observación guardada");
      } catch (error) {
        console.log(error);
      } finally {
        this.savingObservation = false;
      }
    },

    async getInstitution() {
      const institutionId =
        this.selectedSlot.institution?._id || this.selectedSlot.institution;
      if (!institutionId) return;
      this.institution = await institutionService.getInstitutionById(
        institutionId
      );
    },

    async getPatientById() {
      const patient = await patientService.getById(this.appointment.patient);
      this.selectedPatient = patient;
    },

    setAttentionTypes() {
      this.selectedSlot.attentionType.forEach((type) => {
        this.attentionTypes.push(APPOINTMENTS_TYPES[type]);
      });
    },

    /**
     * @description UC-REG-130 — Toggle al panel de creación inline.
     */
    startCreatingPatient() {
      this.creatingPatient = true;
    },

    /**
     * @description UC-REG-130 — Vuelve al panel de búsqueda preservando
     *              el texto que el usuario ya tipeó (no reseteamos
     *              searchInput).
     */
    cancelCreatingPatient() {
      this.creatingPatient = false;
    },

    /**
     * @description UC-REG-130 — Callback de PatientQuickForm cuando se
     *              creó/asoció el paciente. Selecciona ese paciente para
     *              el turno y vuelve al estado normal del modal.
     * @param {Object} patient
     */
    handlePatientCreated(patient) {
      this.creatingPatient = false;
      this.selectPatient(patient);
    },

    selectPatient(patient) {
      this.selectedPatient = patient;
      this.appointment.patient = patient._id;
    },

    /**
     * @description Deselecciona el paciente actual y limpia el campo
     */
    clearPatient() {
      this.selectedPatient = null;
      this.appointment.patient = null;
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    /**
     * @description Devuelve las iniciales (primera letra nombre + apellido) en mayúsculas
     * @param {Object} patient - Objeto paciente/usuario
     * @returns {string}
     */
    getInitials(patient) {
      const first = (patient?.firstName || "")[0] || "";
      const last = (patient?.lastName || "")[0] || "";
      return (first + last).toUpperCase();
    },

    getClassName(patient) {
      if (this.selectedPatient?._id === patient._id) {
        return "is-active";
      } else {
        return "";
      }
    },

    async createAppointment() {
      const isInstant = !this.selectedSlot._id;

      if (isInstant && this.customStartTime) {
        // UC-REG-079: tratar customStartTime como hora local del tenant.
        // Antes se mandaba al backend SIN offset, asi que el server lo
        // interpretaba como UTC → corrido 3h en el calendario.
        const startDateTime = parseUserInputAR(this.customStartTime);
        const endDateTime = new Date(
          startDateTime.getTime() + this.customDuration * 60000 - 1
        );

        const updatedSlot = {
          ...this.selectedSlot,
          startsAt: toApiUTC(startDateTime),
          endsAt: toApiUTC(endDateTime),
        };
        this.appointment.slot = updatedSlot;
      }

      this.isPosting = true;
      try {
        let appointment = await appointmentService.createAppointment({
          ...this.appointment,
          patient: this.isPatient ? this.user._id : this.appointment.patient,
        });

        if (isInstant) {
          const now = new Date();
          const appointmentStart = new Date(appointment.startsAt);

          if (appointmentStart <= now) {
            appointment = await appointmentService.startAppointment(
              appointment._id
            );
          }
        }

        this.$bus.$emit("appointment-created", this.selectedSlot, appointment);
        this.$message.success("Turno asignado con éxito");
        await this.saveNewAppointmentObservation(appointment);
      } catch (error) {
        console.log(error);
      }

      this.isPosting = false;
      this.close();
    },

    /**
     * @description UC-REG-275 — Al agendar, guarda la observación cargada (si
     *              hay). Si falla, el turno ya quedó: se avisa sin tratarlo
     *              como error del turno.
     * @param {Object} appointment - Cita recién creada
     */
    async saveNewAppointmentObservation(appointment) {
      if (this.isPatient || !this.observation.text.trim()) return;
      try {
        const saved = await observationService.save(
          appointment._id,
          this.observation
        );
        this.$bus.$emit(
          "appointment-observation-saved",
          appointment._id,
          saved
        );
      } catch (error) {
        this.$message.warning(
          "El turno se agendó, pero no se pudo guardar la observación."
        );
      }
    },

    cancelAppointment() {
      this.$confirm(`¿Desea cancelar la cita?`, "Cancelar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      })
        .then(() => {
          this.isDeleting = true;
          appointmentService
            .cancelAppointment(this.appointment._id)
            .then((cancelledAppointment) => {
              this.$bus.$emit("appointment-cancelled", cancelledAppointment);
              this.$message.success("Turno cancelado con éxito");
              this.isDeleting = false;
              this.close();
            });
        })
        .catch(() => {});
    },

    /**
     * @description UC-REG-079: render de hora del slot en TZ del tenant.
     */
    getStartAndEndHours(slot) {
      const parsedStartHour = formatTimeAR(slot.startsAt);
      // endsAt suele guardarse como :59.999; sumamos 1ms para mostrar la
      // hora "redonda" siguiente (regla histórica del modal).
      let parsedEndHour = formatTimeAR(
        addMilliseconds(new Date(slot.endsAt), 1)
      );

      // workaround
      if (parsedEndHour === "00:00") {
        parsedEndHour = "24:00";
      }
      // workaround

      return `${parsedStartHour} - ${parsedEndHour}`;
    },

    async goToPatientVideoRoom() {
      this.$router.push({
        name: "appointment",
        params: { appointmentId: this.appointment._id },
      });
    },

    getMedicalInsuranceNumber,

    getMedicalSpecialty() {
      return this.isNewAppointment
        ? this.selectedMedicalSpecialty?.name
        : this.appointment?.medicalSpecialty?.name ||
            this.selectedAppointment?.medicalSpecialty?.name;
    },

    submit() {
      // UC-REG-270: el validador "Paciente" solo existe en la vista de
      // búsqueda; con el alta rápida abierta hay que frenar acá.
      const reason = getPatientBlockReason({
        isPatient: this.isPatient,
        patientId: this.appointment.patient,
        creatingPatient: this.creatingPatient,
      });
      if (reason) {
        this.$message.warning(reason);
        return;
      }
      this.createAppointment();
    },

    /**
     * @description Admite al paciente del turno actual (status pending → admitted)
     */
    async admitFromDetail() {
      this.admitting = true;
      try {
        await appointmentService.admitAppointment(this.appointment._id);
        this.appointment.status = "admitted";
        this.$message.success("Paciente admitido a sala de espera");
        this.$bus.$emit("appointment-admitted", this.appointment);
      } catch {
        this.$message.error("Error al admitir el paciente");
      } finally {
        this.admitting = false;
      }
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog(
  :visible.sync="showDialogModal"
  :before-close="close"
  custom-class="appointment-modal"
  width="760px"
  :show-close="false"
)
  ValidationObserver(v-slot="{ handleSubmit }")
    form(@submit.prevent="handleSubmit(submit)")
      //- ── Header ──
      .modal-header(v-if="isNewAppointment")
        h2.modal-header__title Agendar nueva cita
        .modal-header__actions
          button.btn-ghost(type="button" @click="close" :disabled="isPosting") Cancelar
          button.btn-primary(type="submit" :disabled="isPosting") {{ isPosting ? 'Agendando...' : 'Agendar' }}

      .modal-header(v-else)
        .modal-header__title-row
          h2.modal-header__title Cita asignada
          span.appt-status(
            v-if="appointmentStatus"
            :class="appointmentStatus.className"
          ) {{ appointmentStatus.label }}
        .modal-header__actions
          el-button(
            v-if="!isNewAppointment && appointment.status === 'pending'"
            type="success"
            size="small"
            :loading="admitting"
            @click="admitFromDetail"
          ) Admitir paciente
          button.btn-ghost.btn-ghost--danger(
            v-if="canCancelAppointment"
            type="button"
            @click="cancelAppointment"
            :disabled="isDeleting"
          ) {{ isDeleting ? 'Cancelando...' : 'Cancelar cita' }}
          button.btn-primary(type="button" @click="close") Cerrar

      //- ── Body ──
      .modal-body
        //- Nueva cita: layout de dos columnas
        .modal-two-col(v-if="isNewAppointment")

          //- Columna izquierda: datos del turno
          .modal-col--left
            .form-section
              h3.form-section__title Datos de la cita

              template(v-if="!isInstantAppointment")
                .form-row.form-row--2col
                  .form-field
                    label Fecha
                    p.field-value {{ selectedSlot.startsAt | formatDate }}
                  .form-field
                    label Horario
                    p.field-value {{ getStartAndEndHours(selectedSlot) }}
                .form-field(v-if="isVideoRoomAvailable" style="margin-bottom:12px")
                  el-button.room-button(type="info" :loading="isPosting" @click="goToPatientVideoRoom") Ingresar a la sala

              template(v-if="isInstantAppointment")
                .form-row
                  .form-field
                    label Fecha y Horario *
                    ValidationProvider(name="Fecha y Hora" rules="required|includeToday" v-slot="{ errors }")
                      el-date-picker(
                        v-model="customStartTime"
                        type="datetime"
                        placeholder="Seleccione fecha y hora"
                        format="dd/MM/yyyy HH:mm"
                        lang="es"
                        :not-before="new Date()"
                      )
                      span.has-error {{ errors[0] }}
                .form-row
                  .form-field
                    label Duración (minutos)
                    ValidationProvider(name="Duración" rules="required|integer|min_value:5" v-slot="{ errors }")
                      el-input(v-model="customDuration" type="number")
                      span.has-error {{ errors[0] }}

              .form-row
                .form-field
                  label Doctor
                  p.field-value {{ getFullName(selectedSlot.doctor) || getFullName(selectedAppointment && selectedAppointment.doctor) }}
              .form-row.form-row--2col
                .form-field
                  label Especialidad
                  p.field-value {{ getMedicalSpecialty() }}
                .form-field
                  label Lugar
                  p.field-value(v-if="institution") {{ institution.name }}
              .form-row
                .form-field
                  label Modalidad *
                  ValidationProvider(name="Modalidad" rules="required" v-slot="{ errors }")
                    el-select(v-model="appointment.attentionType" placeholder="Seleccionar modalidad" style="width:100%")
                      el-option(
                        v-for="type in attentionTypes"
                        :key="type.code"
                        :label="type.friendlyName"
                        :value="type.value"
                      )
                    span.has-error {{ errors[0] }}
              //- UC-REG-275 — observación del turno (solo staff)
              .form-row(v-if="!isPatient")
                .form-field
                  label Observaciones
                  el-input.observation-input(
                    v-model="observation.text"
                    type="textarea"
                    :rows="2"
                    :maxlength="observationMaxLength"
                    show-word-limit
                    placeholder="Ej.: trae estudios, viene acompañado"
                  )
                  el-checkbox.observation-visible(v-model="observation.visibleToPatient") Visible para el paciente

          //- Columna derecha: paciente
          .modal-col--right
            .form-section.form-section--patient
              h3.form-section__title Paciente

              //- Portal paciente: ya está logueado
              template(v-if="isPatient")
                .selected-patient
                  .selected-patient__avatar {{ getInitials(user) }}
                  .selected-patient__info
                    .selected-patient__name {{ getFullName(user) }}

              //- Admin/Doctor: selección de paciente
              template(v-else)
                .selected-patient(v-if="selectedPatient")
                  .selected-patient__avatar {{ getInitials(selectedPatient) }}
                  .selected-patient__info
                    .selected-patient__name {{ getFullName(selectedPatient) }}
                    .selected-patient__details
                      | {{ selectedPatient.governmentId && selectedPatient.governmentId.number || '—' }}
                      |  · {{ selectedPatient.email || '—' }}
                      |  · {{ selectedPatient.phone || '—' }}
                  button.btn-ghost.btn-sm(type="button" @click="clearPatient") Cambiar

                template(v-else)
                  //- UC-REG-130: si creatingPatient está activo, mostramos
                  //- el quick form en lugar del buscador. Al confirmar o
                  //- cancelar, vuelve al buscador.
                  PatientQuickForm(
                    v-if="creatingPatient"
                    :prefill-name="searchInput"
                    @created="handlePatientCreated"
                    @cancel="cancelCreatingPatient"
                  )
                  template(v-else)
                    ValidationProvider(name="Paciente" rules="required" v-slot="{ errors }")
                      input(type="hidden" :value="appointment.patient")
                      span.has-error(v-if="errors[0]") {{ errors[0] }}
                    el-input.patient-search(
                      prefix-icon="el-icon-search"
                      v-model="searchInput"
                      @input="debounceInput"
                      placeholder="Buscar paciente..."
                      clearable
                      size="small"
                    )
                    .patient-list
                      spinner(v-if="isLoading")
                      template(v-else)
                        .patient-list__item(
                          v-for="patient in patients"
                          :key="patient._id"
                          @click="selectPatient(patient)"
                        )
                          .patient-list__avatar {{ getInitials(patient) }}
                          .patient-list__info
                            .patient-list__name {{ getFullName(patient) }}
                            .patient-list__doc {{ patient.governmentId && patient.governmentId.number || '—' }}
                          .patient-list__insurance {{ getMedicalInsuranceNumber(patient) || '—' }}
                        .patient-list__empty(v-if="!patients.length") No se encontraron pacientes
                    //- UC-REG-130: botón de creación inline (visible siempre
                    //- en modo búsqueda; el rol sin patient.create no debería
                    //- llegar acá porque no asigna turnos).
                    .patient-list__create-cta(v-if="!isLoading")
                      el-button(type="text" size="small" @click="startCreatingPatient" v-auth="'patient.create'")
                        i.el-icon-plus
                        |  Crear paciente nuevo
                    pagination(
                      :isLoading="isLoading"
                      :limit="pagination.limit"
                      :total="pagination.total"
                      @pagination="setPagination"
                    )

        //- Cita existente: columna única
        template(v-else)
          .form-section
            h3.form-section__title Datos de la cita

            template(v-if="!isInstantAppointment")
              .form-row.form-row--3col
                .form-field
                  label Fecha
                  p.field-value {{ selectedSlot.startsAt | formatDate }}
                .form-field
                  label Horario
                  p.field-value {{ getStartAndEndHours(selectedSlot) }}
                .form-field(v-if="isVideoRoomAvailable")
                  el-button.room-button(type="info" :loading="isPosting" @click="goToPatientVideoRoom") Ingresar a la sala

            .form-row.form-row--3col
              .form-field
                label Doctor
                p.field-value {{ getFullName(selectedSlot.doctor) || getFullName(selectedAppointment.doctor) }}
              .form-field
                label Especialidad
                p.field-value {{ getMedicalSpecialty() }}
              .form-field
                label Lugar
                p.field-value(v-if="institution") {{ institution.name }}

            .form-row(v-if="appointment.attentionType")
              .form-field
                label Modalidad
                p.field-value {{ appointmentTypes[appointment.attentionType].friendlyName }}

          .form-section(v-if="selectedPatient")
            h3.form-section__title Paciente
            .selected-patient
              .selected-patient__avatar {{ getInitials(selectedPatient) }}
              .selected-patient__info
                .selected-patient__name {{ getFullName(selectedPatient) }}
                .selected-patient__details
                  | {{ selectedPatient.governmentId && selectedPatient.governmentId.number || '—' }}
                  |  · {{ selectedPatient.email || '—' }}
                  |  · {{ selectedPatient.phone || '—' }}
                .selected-patient__details(v-if="patientCoverage") Cobertura: {{ patientCoverage }}

          //- UC-REG-275 — observación del turno. Staff: ver / editar.
          //- Paciente: solo la ve si está marcada como visible.
          .form-section(v-if="!isPatient || savedObservation")
            h3.form-section__title Observaciones
            template(v-if="editingObservation")
              el-input.observation-input(
                v-model="observation.text"
                type="textarea"
                :rows="3"
                :maxlength="observationMaxLength"
                show-word-limit
                placeholder="Ej.: trae estudios, viene acompañado"
              )
              .observation-edit__footer
                el-checkbox(v-model="observation.visibleToPatient") Visible para el paciente
                .observation-edit__actions
                  button.btn-ghost(type="button" @click="editingObservation = false" :disabled="savingObservation") Cancelar
                  button.btn-primary(type="button" @click="saveObservation" :disabled="savingObservation") {{ savingObservation ? 'Guardando...' : 'Guardar' }}
            template(v-else)
              p.observation-text(v-if="savedObservation") {{ savedObservation.text }}
              p.observation-empty(v-else) Sin observaciones
              .observation-view__footer(v-if="!isPatient")
                span.observation-badge(v-if="savedObservation && savedObservation.visibleToPatient")
                  i.el-icon-view
                  |  Visible para el paciente
                span.observation-badge.observation-badge--private(v-else-if="savedObservation")
                  i.el-icon-lock
                  |  Solo para el equipo
                span(v-else)
                button.btn-ghost.btn-sm(type="button" @click="startEditObservation") {{ savedObservation ? 'Editar' : 'Agregar' }}
</template>

<style lang="scss" scoped>
/* ── Dialog overrides ── */
::v-deep .appointment-modal {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}

/* ── Observaciones (UC-REG-275) ── */
/* El global vendor/element/textarea.scss fuerza min-height 145px !important */
.observation-input ::v-deep .el-textarea__inner {
  min-height: 56px !important;
}

.form-field .observation-visible,
.observation-edit__footer .el-checkbox {
  display: inline-flex;
  margin-top: 6px;
  text-transform: none;
  font-weight: 500;
  font-size: 12px;
  color: #475569;
}

.observation-text {
  margin: 0 0 8px;
  font-size: 13px;
  color: #1e293b;
  white-space: pre-wrap;
  word-break: break-word;
}

.observation-empty {
  margin: 0 0 8px;
  font-size: 13px;
  color: #94a3b8;
}

.observation-view__footer,
.observation-edit__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.observation-edit__actions {
  display: flex;
  gap: 8px;
}

.observation-badge {
  font-size: 12px;
  color: #0c447c;

  &--private {
    color: #64748b;
  }
}

/* ── Header ── */
.modal-header__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header__title {
  font-size: 18px;
  font-weight: 700;
  color: $navy;
  margin: 0;
}

.modal-header__actions {
  display: flex;
  gap: 10px;
}

/* ── Buttons ── */
.btn-primary {
  padding: 8px 20px;
  background: $navy;
  color: white;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-ghost {
  padding: 8px 20px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--danger {
    color: #dc2626;
    border-color: #fecaca;

    &:hover {
      background: #fef2f2;
      border-color: #fca5a5;
    }
  }
}

.btn-sm {
  padding: 4px 12px;
  font-size: 12px;
}

/* ── Body ── */
.modal-body {
  padding: 20px 24px;
}

/* ── Dos columnas (nueva cita) ── */
.modal-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: 440px; /* altura fija: el modal no cambia de tamaño */
}

.modal-col--left,
.modal-col--right {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Ambas form-section llenan toda la altura de su columna */
.modal-col--left .form-section,
.form-section--patient {
  flex: 1;
  min-height: 0;
  margin-bottom: 0;
}

/* UC-REG-275: con Observaciones (y en sobreturno, fecha + duración) la
   columna izquierda puede no entrar en la altura fija: scrollea adentro. */
.modal-col--left .form-section {
  overflow-y: auto;
}

/* La sección paciente es flex para que la lista ocupe el espacio restante */
.form-section--patient {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Form sections ── */
.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-section__title {
  font-size: 13px;
  font-weight: 700;
  color: $navy;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Form layout ── */
.form-row {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-row--3col {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.form-row--2col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-field {
  label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    margin-bottom: 4px;
    text-transform: uppercase;
  }
}

.field-value {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
  margin: 0;
}

/* ── Selected patient card ── */
.selected-patient {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: $teal-light;
  border: 1px solid $primary;
  border-radius: 10px;
}

.selected-patient__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: $navy;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.selected-patient__info {
  flex: 1;
}

.selected-patient__name {
  font-weight: 600;
  color: $navy;
  font-size: 14px;
}

.selected-patient__details {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* ── Patient search ── */
.patient-search {
  margin-bottom: 12px;
}

::v-deep .patient-search .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* ── Patient list ── */
.patient-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.patient-list__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $teal-light;
  }
}

.patient-list__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f5f9;
  color: $navy;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.patient-list__info {
  flex: 1;
}

.patient-list__name {
  font-weight: 600;
  font-size: 13px;
  color: #334155;
}

.patient-list__doc {
  font-size: 11px;
  color: #94a3b8;
}

.patient-list__insurance {
  font-size: 11px;
  color: #94a3b8;
}

.patient-list__empty {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

/* UC-REG-130 */
.patient-list__create-cta {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  border-top: 1px solid #eef0f3;
  margin-top: 4px;
}

/* ── Video room button ── */
.room-button {
  border: none;
  background-color: rgb(113, 215, 135);
  margin-top: 16px;
}

/* ── Validation errors ── */
.has-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .form-row--3col {
    grid-template-columns: 1fr;
  }

  .form-row--2col {
    grid-template-columns: 1fr;
  }

  .modal-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>
